import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { jwtDecode } from 'jwt-decode'
import authService from '@/services/authService'
import impersonateService from '@/services/impersonateService'
import apiClient from '@/services/api'
import { isTokenExpired } from '@/utils/auth'

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref(localStorage.getItem('access_token'))
  const refreshToken = ref(localStorage.getItem('refresh_token'))
  const user = ref(null)
  const customerIds = ref([])
  const userRole = ref(localStorage.getItem('user_role') || null)

  // Impersonation state
  const isImpersonating = ref(false)
  const impersonatedBy = ref(null)
  const originalToken = ref(sessionStorage.getItem('original_access_token'))
  const originalRefreshToken = ref(sessionStorage.getItem('original_refresh_token'))
  const isEpAdmin = ref(localStorage.getItem('is_ep_admin') === 'true')
  // True once fetchUserRole has resolved against the server this session.
  // The router guard awaits a first hydration before deciding role-gated
  // routes, so a deep-link / refresh to e.g. /impersonate is not bounced by
  // the not-yet-loaded isEpAdmin ref (SEZ-4444-R12).
  const roleFetched = ref(false)
  // True only while tokens are being swapped to start impersonation. The api
  // interceptor reads this to avoid logging the admin out on a transient 401
  // during the exchange (SEZ-4444-R12).
  const impersonationInProgress = ref(false)

  // Validity depends on the wall clock, which is NOT reactive, so these have
  // to be plain functions. As computeds they would cache until `token` itself
  // changed and keep reporting a live session in a tab left open past expiry
  // — exactly the case this ticket is about (SEZ-5044-R04).
  function hasValidAccessToken() {
    return !isTokenExpired(token.value)
  }

  // A usable session: either the access token is valid, or the refresh token
  // can still mint a new one. Access tokens live 24 h and refresh tokens
  // 7 days, so keying this on the access token alone would cut the customer's
  // session from a week to a day (SEZ-5044-R06).
  function hasUsableSession() {
    return hasValidAccessToken() || !isTokenExpired(refreshToken.value)
  }

  // Getters (computed)
  // Template-facing mirrors of the two functions above. They re-evaluate on
  // token changes, which is what a view needs; imperative callers must use
  // the functions, because a view re-render is not what makes a token expire.
  const isAccessTokenValid = computed(() => !isTokenExpired(token.value))
  const isAuthenticated = computed(
    () => isAccessTokenValid.value || !isTokenExpired(refreshToken.value)
  )
  const currentUser = computed(() => user.value)
  const hasCustomers = computed(() => customerIds.value.length > 0)
  const isInitialAccess = computed(() => userRole.value === 'initial' || !userRole.value)
  const isFullAccess = computed(() => userRole.value === 'full')
  const isOperatorAccess = computed(() => userRole.value === 'operator')
  const canImpersonate = computed(() => isEpAdmin.value && !isImpersonating.value)

  // Actions
  async function login(email, password) {
    const response = await authService.login(email, password)
    setTokens(response.data.access_token, response.data.refresh_token)
    user.value = response.data.user
    userRole.value = response.data.user?.role || 'initial'
    localStorage.setItem('user_role', userRole.value)
    decodeToken()
    await fetchCustomerIds()
    await fetchUserRole()
  }

  async function register(userData) {
    const response = await authService.register(userData)
    return response.data
  }

  // A rejected refresh means the session is genuinely over: the endpoint
  // answers 401 for an expired, malformed or revoked token and for a
  // deactivated user. Anything else — a 5xx, a timeout, no response at all —
  // is a fault on the way, and must not cost the customer their session
  // (SEZ-5044-R01).
  function refreshRejectedByBackend(error) {
    const status = error && error.response && error.response.status
    return status === 401 || status === 403
  }

  // N views can 401 at once, and R06 makes that likelier by navigating on a
  // deliberately stale access token. Without memoisation each one mints its
  // own token and the last write wins.
  let refreshInFlight = null

  function refreshAccessToken() {
    if (!refreshInFlight) {
      refreshInFlight = authService
        .refreshToken(refreshToken.value)
        .then(response => {
          setTokens(response.data.access_token, refreshToken.value)
          decodeToken()
        })
        .finally(() => {
          refreshInFlight = null
        })
    }
    return refreshInFlight
  }

  async function fetchUserRole({ throwOnError = false } = {}) {
    try {
      const response = await apiClient.get('/enercare/api/v1/roles/me/')
      userRole.value = response.data.role || 'initial'
      localStorage.setItem('user_role', userRole.value)
      isEpAdmin.value = !!response.data.is_ep_admin
      localStorage.setItem('is_ep_admin', isEpAdmin.value ? 'true' : 'false')
      roleFetched.value = true
    } catch (error) {
      if (throwOnError) throw error
      // Keep existing value on failure
    }
  }

  async function fetchCustomerIds({ throwOnError = false } = {}) {
    try {
      const response = await apiClient.get('/enercare/api/v1/auth/verify/')
      customerIds.value = response.data.customer_ids || []
      if (response.data.user) {
        user.value = response.data.user
      }
    } catch (error) {
      if (throwOnError) throw error
      customerIds.value = []
    }
  }

  function setTokens(accessToken, refreshTokenValue) {
    token.value = accessToken
    refreshToken.value = refreshTokenValue
    localStorage.setItem('access_token', accessToken)
    localStorage.setItem('refresh_token', refreshTokenValue)
  }

  function decodeToken({ logoutOnError = true } = {}) {
    if (token.value) {
      try {
        const decoded = jwtDecode(token.value)
        if (!user.value) {
          user.value = {
            id: decoded.user_id,
            email: decoded.email
          }
        }
        if (decoded.impersonated_by) {
          isImpersonating.value = true
          impersonatedBy.value = decoded.impersonated_by
        }
      } catch (error) {
        console.error('Failed to decode token:', error)
        if (logoutOnError) {
          clearSession()
        } else {
          throw error
        }
      }
    }
  }

  // Restore the admin session saved before impersonation started. Used when
  // the impersonation start flow fails, so the admin is NOT logged out
  // (SEZ-4444-R12).
  function restoreAdminSession() {
    const adminAccess =
      originalToken.value || sessionStorage.getItem('original_access_token')
    const adminRefresh =
      originalRefreshToken.value ||
      sessionStorage.getItem('original_refresh_token')

    isImpersonating.value = false
    impersonatedBy.value = null
    user.value = null

    if (adminAccess && adminRefresh) {
      setTokens(adminAccess, adminRefresh)
      try {
        decodeToken({ logoutOnError: false })
      } catch {
        // Admin token is unusable — leave user.value null rather than forcing
        // a logout; cleanup below must still run.
      }
    }

    sessionStorage.removeItem('original_access_token')
    sessionStorage.removeItem('original_refresh_token')
    originalToken.value = null
    originalRefreshToken.value = null
  }

  // Single path for every automatic session teardown (failed refresh,
  // undecodable token, unusable session at startup). When admin tokens are
  // parked for a return from impersonation, the admin session is restored
  // instead of destroyed (SEZ-5044-R07).
  function clearSession() {
    const hasParkedAdminSession =
      !!originalToken.value ||
      !!sessionStorage.getItem('original_access_token')

    if (hasParkedAdminSession) {
      restoreAdminSession()
      return
    }

    logout()
  }

  async function startImpersonation(userId) {
    originalToken.value = token.value
    originalRefreshToken.value = refreshToken.value
    sessionStorage.setItem('original_access_token', token.value)
    sessionStorage.setItem('original_refresh_token', refreshToken.value)

    impersonationInProgress.value = true
    try {
      const response = await impersonateService.startImpersonation(userId)
      setTokens(response.data.access_token, response.data.refresh_token)
      user.value = null
      isImpersonating.value = false
      impersonatedBy.value = null
      // Don't log out if the impersonation token can't be decoded — restore
      // the admin session instead (handled by the catch below).
      decodeToken({ logoutOnError: false })
      await fetchUserRole({ throwOnError: true })
      await fetchCustomerIds({ throwOnError: true })
    } catch (error) {
      restoreAdminSession()
      throw error
    } finally {
      impersonationInProgress.value = false
    }
  }

  async function stopImpersonation() {
    await impersonateService.stopImpersonation()

    setTokens(originalToken.value, originalRefreshToken.value)
    sessionStorage.removeItem('original_access_token')
    sessionStorage.removeItem('original_refresh_token')
    originalToken.value = null
    originalRefreshToken.value = null
    isImpersonating.value = false
    impersonatedBy.value = null
    user.value = null
    decodeToken()
    await fetchUserRole()
    await fetchCustomerIds()
  }

  function logout() {
    token.value = null
    refreshToken.value = null
    user.value = null
    customerIds.value = []
    userRole.value = null
    isImpersonating.value = false
    impersonatedBy.value = null
    isEpAdmin.value = false
    roleFetched.value = false
    originalToken.value = null
    originalRefreshToken.value = null
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user_role')
    localStorage.removeItem('is_ep_admin')
    sessionStorage.removeItem('original_access_token')
    sessionStorage.removeItem('original_refresh_token')
  }

  // Runs at store creation, inside the router guard, before any component
  // mounts. It issues NO requests: leftovers of a dead session are dropped,
  // everything else waits for a route that actually needs a session
  // (SEZ-5044-R08).
  function initSession() {
    if (!token.value && !refreshToken.value) return

    if (!hasUsableSession()) {
      // Nothing left that could carry a session. Clearing here also strips
      // the stale role and admin flag, so a public page shows no data from
      // the account that used to be logged in (SEZ-5044-R01, SEZ-5044-R04).
      clearSession()
    }
  }

  let hydrated = false

  // Brings the session up to date for a route that needs one: refresh a
  // stale access token first, then hydrate role and customer ids. Public
  // routes never call this, which is what keeps `/pay` free of logged-in
  // traffic (SEZ-5044-R06, SEZ-5044-R08).
  async function beginSession() {
    if (!hasUsableSession()) {
      clearSession()
      return
    }

    if (!hasValidAccessToken()) {
      try {
        await refreshAccessToken()
      } catch (error) {
        // Only a refusal from the backend ends the session. A fault on the
        // way keeps it, so a second tab is not logged out by a blip
        // (SEZ-5044-R01).
        if (refreshRejectedByBackend(error)) clearSession()
        return
      }
    }

    decodeToken()

    if (hydrated) return
    hydrated = true
    // Deliberately not awaited: the guard waits on beginSession, and these
    // two would make navigation wait for requests whose own interceptor can
    // end up waiting for the router.
    fetchUserRole()
    fetchCustomerIds()
  }

  initSession()

  // Awaited by the router guard and by components that query logged-in
  // endpoints outside router-view, so they never race the refresh above.
  // Resolved until a protected navigation replaces it; a rejection here
  // would abort every navigation for the lifetime of the tab.
  const sessionReady = ref(Promise.resolve())

  function startSession() {
    sessionReady.value = beginSession().catch(() => {})
    return sessionReady.value
  }

  return {
    // State
    token,
    refreshToken,
    user,
    customerIds,
    userRole,
    isImpersonating,
    impersonatedBy,
    originalToken,
    originalRefreshToken,
    isEpAdmin,
    roleFetched,
    impersonationInProgress,
    sessionReady,

    // Getters
    isAuthenticated,
    isAccessTokenValid,
    currentUser,
    hasCustomers,
    isInitialAccess,
    isFullAccess,
    isOperatorAccess,
    canImpersonate,

    // Actions
    login,
    register,
    refreshAccessToken,
    refreshRejectedByBackend,
    hasValidAccessToken,
    hasUsableSession,
    startSession,
    logout,
    clearSession,
    setTokens,
    decodeToken,
    fetchUserRole,
    fetchCustomerIds,
    startImpersonation,
    stopImpersonation
  }
})
